import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Wind, Gauge, Battery, Brain, Sparkles, Timer } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import RobotRecommendationWidget from "@/components/RobotRecommendationWidget";
import RobotPageNav from "@/components/RobotPageNav";
import mt1VacImg from "@/assets/robots/mt1-vac-hero.png";

const MT1Vac = () => {
  const specifications = [
    {
      icon: <Wind className="w-5 h-5" />,
      label: "Cleaning Width",
      value: "55cm"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Suction Power",
      value: "High Performance"
    },
    {
      icon: <Battery className="w-5 h-5" />,
      label: "Runtime",
      value: "24/7 Operation"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      label: "Intelligence",
      value: "AI-Powered"
    }
  ];

  const keyFeatures = [
    {
      icon: <Wind className="w-6 h-6" />,
      title: "Powerful Suction System",
      description: "Advanced 55cm cleaning width with powerful suction technology ensures maximum coverage and thorough debris removal in a single pass."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "AI-Driven Adaptive Cleaning",
      description: "Smart debris detection and response system automatically adjusts cleaning intensity based on surface conditions and debris types."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "Dual Cleaning Technology",
      description: "Combined sweeping and vacuuming capabilities tackle both fine dust and larger debris with exceptional efficiency."
    },
    {
      icon: <Timer className="w-6 h-6" />,
      title: "24/7 Continuous Operation",
      description: "Extended runtime capability enables round-the-clock cleaning operations for large facilities without interruption."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "Quick-Release Dust Module",
      description: "Easy-to-remove dust collection system simplifies maintenance and reduces downtime for cleaning staff."
    },
    {
      icon: <Wind className="w-6 h-6" />,
      title: "Hand-Vacuum Extension",
      description: "Versatile hand-vacuum attachment enables detailed cleaning in tight spaces, corners, and hard-to-reach areas."
    }
  ];

  const applications = [
    {
      title: "Shopping Malls & Retail",
      description: "Maintain pristine floors throughout high-traffic retail spaces with continuous automated cleaning during operating hours."
    },
    {
      title: "Office Buildings",
      description: "Keep corporate environments clean with scheduled autonomous sweeping and vacuuming for lobbies, hallways, and common areas."
    },
    {
      title: "Warehouses & Distribution",
      description: "Handle industrial debris and dust in large warehouse spaces with powerful suction and wide cleaning coverage."
    },
    {
      title: "Airports & Transportation",
      description: "Manage constant foot traffic and debris in terminals, concourses, and waiting areas with 24/7 operation capability."
    },
    {
      title: "Hotels & Hospitality",
      description: "Maintain guest satisfaction with immaculate public spaces including lobbies, conference rooms, and corridors."
    },
    {
      title: "Educational Facilities",
      description: "Keep schools and universities clean with efficient sweeping and vacuuming of hallways, cafeterias, and common spaces."
    }
  ];

  return (
    <>
      <SEO
        title="PUDU MT1 Vac - AI-Powered Robotic Sweeper & Vacuum | Neil AI"
        description="Advanced sweeping and vacuuming robot with AI-powered cleaning intelligence, 55cm cleaning width, and 24/7 continuous operation. Perfect for commercial and industrial spaces."
        keywords="MT1 Vac, robotic sweeper, robotic vacuum, AI cleaning, commercial cleaning robot, automated sweeping, industrial vacuum"
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <RobotRecommendationWidget />
        
        <main className="container mx-auto px-4 pt-12 pb-8 md:pt-16 md:pb-12">
          <RobotPageNav />

          {/* Hero Section */}
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12">
            <div className="space-y-6">
              <div>
                <Badge className="mb-4">AI-Powered Sweeper & Vacuum</Badge>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">
                  PUDU MT1 Vac
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Advanced sweeping and vacuuming robot with AI-powered cleaning intelligence and extended runtime for 24/7 continuous operation.
                </p>
              </div>

              {/* Quick Specs */}
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {specifications.map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-3 md:p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <span className="text-xs font-semibold">{spec.label}</span>
                      </div>
                      <p className="text-xs md:text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button asChild size="lg" className="flex-1">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="flex-1">
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg" className="flex-1">
                  <a href="tel:+13369624539">
                    Call Us: (336) 962-4539
                  </a>
                </Button>
              </div>

              {/* ROI Highlight */}
              <Card className="glass-effect border-primary/30 bg-primary/5">
                <CardContent className="p-4 md:p-6">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-bold mb-1 text-sm md:text-base">ROI Impact</h3>
                      <p className="text-xs md:text-sm text-muted-foreground">
                        Reduce cleaning labor costs by up to 60% while improving cleaning consistency and coverage with autonomous 24/7 operation.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square md:aspect-auto md:h-full rounded-lg overflow-hidden bg-white/5 flex items-center justify-center p-4 md:p-8">
                <img 
                  src={mt1VacImg} 
                  alt="PUDU MT1 Vac - AI-Powered Robotic Sweeper & Vacuum"
                  className="w-full h-full object-contain"
                />
              </div>
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">
                NEW
              </Badge>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specs">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Key Features</CardTitle>
                  <CardDescription>
                    Advanced AI-powered cleaning technology for superior performance
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {keyFeatures.map((feature, idx) => (
                      <div key={idx} className="flex gap-3 md:gap-4">
                        <div className="text-primary flex-shrink-0">{feature.icon}</div>
                        <div>
                          <h4 className="font-bold mb-1 md:mb-2 text-sm md:text-base">{feature.title}</h4>
                          <p className="text-xs md:text-sm text-muted-foreground">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="specs" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Technical Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 md:space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Cleaning Width</span>
                      <span className="text-sm md:text-base">55cm Maximum Coverage</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Cleaning Technology</span>
                      <span className="text-sm md:text-base">Sweeping + Vacuuming</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Navigation</span>
                      <span className="text-sm md:text-base">VSLAM+ Positioning</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">AI Features</span>
                      <span className="text-sm md:text-base">Trash Recognition & Adaptive Cleaning</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Operation Mode</span>
                      <span className="text-sm md:text-base">24/7 Continuous Operation</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pb-3 md:pb-4 border-b border-[hsl(var(--line))]">
                      <span className="font-semibold text-sm md:text-base">Maintenance</span>
                      <span className="text-sm md:text-base">Quick-Release Dust Module</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      <span className="font-semibold text-sm md:text-base">Additional Features</span>
                      <span className="text-sm md:text-base">Hand-Vacuum Extension</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron">Ideal Applications</CardTitle>
                  <CardDescription>
                    Perfect for various commercial and industrial environments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="p-3 md:p-4 rounded-lg border border-[hsl(var(--line))] hover:border-primary/50 transition-colors">
                        <h4 className="font-bold mb-2 text-sm md:text-base">{app.title}</h4>
                        <p className="text-xs md:text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Final CTA */}
          <Card className="glass-effect border-primary/30 bg-primary/5 mt-8 md:mt-12">
            <CardContent className="p-6 md:p-8 text-center">
              <h2 className="font-orbitron font-bold text-xl md:text-2xl mb-4">
                Ready to Transform Your Cleaning Operations?
              </h2>
              <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Experience the future of automated cleaning with PUDU MT1 Vac's AI-powered sweeping and vacuuming technology.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                <Button asChild size="lg">
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Schedule a Demo
                  </a>
                </Button>
                <Button asChild variant="secondary" size="lg">
                  <Link to="/robot-recommender">
                    Find Your Perfect Robot
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MT1Vac;
